import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { accountingApi } from '@api/accountingApi';
import type {
  OrganizationFilters, CreateOrganizationRequest, UpdateOrganizationRequest,
  CounterpartyFilters, CreateCounterpartyRequest, UpdateCounterpartyRequest,
  CreateCurrencyRequest, UpdateCurrencyRequest,
  CreateBankRequest, UpdateBankRequest,
  BankAccountFilters, CreateBankAccountRequest, UpdateBankAccountRequest,
  CreateNomenclatureGroupRequest, UpdateNomenclatureGroupRequest,
  NomenclatureFilters, CreateNomenclatureRequest, UpdateNomenclatureRequest,
  CreateWarehouseRequest, UpdateWarehouseRequest,
  CreateCostItemRequest, UpdateCostItemRequest,
  CreatePriceTypeRequest, UpdatePriceTypeRequest,
  ChartOfAccountFilters, CreateChartOfAccountRequest, UpdateChartOfAccountRequest,
  CreateTaxActivityTypeRequest, UpdateTaxActivityTypeRequest,
  CreateSettlementTypeRequest, UpdateSettlementTypeRequest,
  CreateOrgDepartmentRequest, UpdateOrgDepartmentRequest,
  ContractFilters, CreateContractRequest, UpdateContractRequest,
  CreateCurrencyRateRequest, UpdateCurrencyRateRequest,
  NomenclaturePriceItem, AccountingPolicyItem,
  TrialBalanceFilters,
  JournalEntryFilters,
  IndividualFilters, CreateIndividualRequest, UpdateIndividualRequest,
  EmployeeFilters, CreateEmployeeRequest, UpdateEmployeeRequest,
  // Position-types removed (Етап 4f)
  CreateFixedAssetRequest, UpdateFixedAssetRequest,
  CreateIntangibleAssetRequest, UpdateIntangibleAssetRequest,
} from '@/types/accounting';

// =============================================
// Organizations
// =============================================
export function useOrganizations(acqFilters: OrganizationFilters) {
  return useQuery({
    queryKey: ['accounting', 'organizations', acqFilters],
    queryFn: () => accountingApi.getOrganizations(acqFilters),
    placeholderData: (acqPrev) => acqPrev,
  });
}
export function useCreateOrganization() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: CreateOrganizationRequest) => accountingApi.createOrganization(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'organizations'] }); message.success('Організацію створено'); },
    onError: () => { message.error('Помилка створення організації'); },
  });
}
export function useUpdateOrganization() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: UpdateOrganizationRequest) => accountingApi.updateOrganization(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'organizations'] }); message.success('Організацію оновлено'); },
    onError: () => { message.error('Помилка оновлення організації'); },
  });
}
export function useDeleteOrganization() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => accountingApi.deleteOrganization(acqId),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'organizations'] }); message.success('Організацію видалено'); },
    onError: () => { message.error('Помилка видалення організації'); },
  });
}

// =============================================
// Counterparties
// =============================================
export function useCounterparties(acqFilters: CounterpartyFilters) {
  return useQuery({
    queryKey: ['accounting', 'counterparties', acqFilters],
    queryFn: () => accountingApi.getCounterparties(acqFilters),
    placeholderData: (acqPrev) => acqPrev,
  });
}
export function useCreateCounterparty() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: CreateCounterpartyRequest) => accountingApi.createCounterparty(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'counterparties'] }); message.success('Контрагента створено'); },
    onError: () => { message.error('Помилка створення контрагента'); },
  });
}
export function useUpdateCounterparty() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: UpdateCounterpartyRequest) => accountingApi.updateCounterparty(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'counterparties'] }); message.success('Контрагента оновлено'); },
    onError: () => { message.error('Помилка оновлення контрагента'); },
  });
}
export function useDeleteCounterparty() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => accountingApi.deleteCounterparty(acqId),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'counterparties'] }); message.success('Контрагента видалено'); },
    onError: () => { message.error('Помилка видалення контрагента'); },
  });
}

// =============================================
// Currencies
// =============================================
export function useCurrencies() {
  return useQuery({
    queryKey: ['accounting', 'currencies'],
    queryFn: () => accountingApi.getCurrencies(),
  });
}
export function useCreateCurrency() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: CreateCurrencyRequest) => accountingApi.createCurrency(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'currencies'] }); message.success('Валюту створено'); },
    onError: () => { message.error('Помилка створення валюти'); },
  });
}
export function useUpdateCurrency() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: UpdateCurrencyRequest) => accountingApi.updateCurrency(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'currencies'] }); message.success('Валюту оновлено'); },
    onError: () => { message.error('Помилка оновлення валюти'); },
  });
}
export function useDeleteCurrency() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => accountingApi.deleteCurrency(acqId),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'currencies'] }); message.success('Валюту видалено'); },
    onError: () => { message.error('Помилка видалення валюти'); },
  });
}

// =============================================
// Banks
// =============================================
export function useBanks() {
  return useQuery({
    queryKey: ['accounting', 'banks'],
    queryFn: () => accountingApi.getBanks(),
  });
}
export function useCreateBank() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: CreateBankRequest) => accountingApi.createBank(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'banks'] }); message.success('Банк створено'); },
    onError: () => { message.error('Помилка створення банку'); },
  });
}
export function useUpdateBank() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: UpdateBankRequest) => accountingApi.updateBank(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'banks'] }); message.success('Банк оновлено'); },
    onError: () => { message.error('Помилка оновлення банку'); },
  });
}
export function useDeleteBank() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => accountingApi.deleteBank(acqId),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'banks'] }); message.success('Банк видалено'); },
    onError: () => { message.error('Помилка видалення банку'); },
  });
}

// =============================================
// Bank Accounts
// =============================================
export function useBankAccounts(acqFilters: BankAccountFilters) {
  return useQuery({
    queryKey: ['accounting', 'bank-accounts', acqFilters],
    queryFn: () => accountingApi.getBankAccounts(acqFilters),
    placeholderData: (acqPrev) => acqPrev,
  });
}
export function useCreateBankAccount() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: CreateBankAccountRequest) => accountingApi.createBankAccount(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'bank-accounts'] }); message.success('Банківський рахунок створено'); },
    onError: () => { message.error('Помилка створення банківського рахунку'); },
  });
}
export function useUpdateBankAccount() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: UpdateBankAccountRequest) => accountingApi.updateBankAccount(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'bank-accounts'] }); message.success('Банківський рахунок оновлено'); },
    onError: () => { message.error('Помилка оновлення банківського рахунку'); },
  });
}
export function useDeleteBankAccount() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => accountingApi.deleteBankAccount(acqId),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'bank-accounts'] }); message.success('Банківський рахунок видалено'); },
    onError: () => { message.error('Помилка видалення банківського рахунку'); },
  });
}

// =============================================
// Nomenclature Groups
// =============================================
export function useNomenclatureGroups() {
  return useQuery({
    queryKey: ['accounting', 'nomenclature-groups'],
    queryFn: () => accountingApi.getNomenclatureGroups(),
  });
}
export function useCreateNomenclatureGroup() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: CreateNomenclatureGroupRequest) => accountingApi.createNomenclatureGroup(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'nomenclature-groups'] }); message.success('Номенклатурну групу створено'); },
    onError: () => { message.error('Помилка створення номенклатурної групи'); },
  });
}
export function useUpdateNomenclatureGroup() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: UpdateNomenclatureGroupRequest) => accountingApi.updateNomenclatureGroup(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'nomenclature-groups'] }); message.success('Номенклатурну групу оновлено'); },
    onError: () => { message.error('Помилка оновлення номенклатурної групи'); },
  });
}
export function useDeleteNomenclatureGroup() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => accountingApi.deleteNomenclatureGroup(acqId),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'nomenclature-groups'] }); message.success('Номенклатурну групу видалено'); },
    onError: () => { message.error('Помилка видалення номенклатурної групи'); },
  });
}

// =============================================
// Nomenclatures
// =============================================
export function useNomenclatures(acqFilters: NomenclatureFilters) {
  return useQuery({
    queryKey: ['accounting', 'nomenclatures', acqFilters],
    queryFn: () => accountingApi.getNomenclatures(acqFilters),
    placeholderData: (acqPrev) => acqPrev,
  });
}
export function useCreateNomenclature() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: CreateNomenclatureRequest) => accountingApi.createNomenclature(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'nomenclatures'] }); message.success('Номенклатуру створено'); },
    onError: () => { message.error('Помилка створення номенклатури'); },
  });
}
export function useUpdateNomenclature() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: UpdateNomenclatureRequest) => accountingApi.updateNomenclature(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'nomenclatures'] }); message.success('Номенклатуру оновлено'); },
    onError: () => { message.error('Помилка оновлення номенклатури'); },
  });
}
export function useDeleteNomenclature() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => accountingApi.deleteNomenclature(acqId),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'nomenclatures'] }); message.success('Номенклатуру видалено'); },
    onError: () => { message.error('Помилка видалення номенклатури'); },
  });
}

// =============================================
// Accounting Warehouses
// =============================================
export function useAccountingWarehouses() {
  return useQuery({
    queryKey: ['accounting', 'warehouses'],
    queryFn: () => accountingApi.getAccountingWarehouses(),
  });
}
export function useCreateAccountingWarehouse() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: CreateWarehouseRequest) => accountingApi.createAccountingWarehouse(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'warehouses'] }); message.success('Склад створено'); },
    onError: () => { message.error('Помилка створення складу'); },
  });
}
export function useUpdateAccountingWarehouse() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: UpdateWarehouseRequest) => accountingApi.updateAccountingWarehouse(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'warehouses'] }); message.success('Склад оновлено'); },
    onError: () => { message.error('Помилка оновлення складу'); },
  });
}
export function useDeleteAccountingWarehouse() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => accountingApi.deleteAccountingWarehouse(acqId),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'warehouses'] }); message.success('Склад видалено'); },
    onError: () => { message.error('Помилка видалення складу'); },
  });
}

// =============================================
// Cost Items
// =============================================
export function useCostItems() {
  return useQuery({
    queryKey: ['accounting', 'cost-items'],
    queryFn: () => accountingApi.getCostItems(),
  });
}
export function useCreateCostItem() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: CreateCostItemRequest) => accountingApi.createCostItem(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'cost-items'] }); message.success('Статтю витрат створено'); },
    onError: () => { message.error('Помилка створення статті витрат'); },
  });
}
export function useUpdateCostItem() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: UpdateCostItemRequest) => accountingApi.updateCostItem(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'cost-items'] }); message.success('Статтю витрат оновлено'); },
    onError: () => { message.error('Помилка оновлення статті витрат'); },
  });
}
export function useDeleteCostItem() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => accountingApi.deleteCostItem(acqId),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'cost-items'] }); message.success('Статтю витрат видалено'); },
    onError: () => { message.error('Помилка видалення статті витрат'); },
  });
}

// =============================================
// Price Types
// =============================================
export function usePriceTypes() {
  return useQuery({
    queryKey: ['accounting', 'price-types'],
    queryFn: () => accountingApi.getPriceTypes(),
  });
}
export function useCreatePriceType() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: CreatePriceTypeRequest) => accountingApi.createPriceType(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'price-types'] }); message.success('Тип цін створено'); },
    onError: () => { message.error('Помилка створення типу цін'); },
  });
}
export function useUpdatePriceType() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: UpdatePriceTypeRequest) => accountingApi.updatePriceType(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'price-types'] }); message.success('Тип цін оновлено'); },
    onError: () => { message.error('Помилка оновлення типу цін'); },
  });
}
export function useDeletePriceType() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => accountingApi.deletePriceType(acqId),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'price-types'] }); message.success('Тип цін видалено'); },
    onError: () => { message.error('Помилка видалення типу цін'); },
  });
}

// =============================================
// Chart of Accounts
// =============================================
export function useChartOfAccounts(acqFilters: ChartOfAccountFilters) {
  return useQuery({
    queryKey: ['accounting', 'chart-of-accounts', acqFilters],
    queryFn: () => accountingApi.getChartOfAccounts(acqFilters),
    placeholderData: (acqPrev) => acqPrev,
  });
}
export function useAllChartOfAccounts() {
  return useQuery({
    queryKey: ['accounting', 'chart-of-accounts', 'all'],
    queryFn: () => accountingApi.getAllChartOfAccounts(),
  });
}
export function useCreateChartOfAccount() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: CreateChartOfAccountRequest) => accountingApi.createChartOfAccount(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'chart-of-accounts'] }); message.success('Рахунок створено'); },
    onError: () => { message.error('Помилка створення рахунку'); },
  });
}
export function useUpdateChartOfAccount() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: UpdateChartOfAccountRequest) => accountingApi.updateChartOfAccount(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'chart-of-accounts'] }); message.success('Рахунок оновлено'); },
    onError: () => { message.error('Помилка оновлення рахунку'); },
  });
}
export function useDeleteChartOfAccount() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => accountingApi.deleteChartOfAccount(acqId),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'chart-of-accounts'] }); message.success('Рахунок видалено'); },
    onError: () => { message.error('Помилка видалення рахунку'); },
  });
}

// =============================================
// Tax Activity Types
// =============================================
export function useTaxActivityTypes() {
  return useQuery({
    queryKey: ['accounting', 'tax-activity-types'],
    queryFn: () => accountingApi.getTaxActivityTypes(),
  });
}
export function useCreateTaxActivityType() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: CreateTaxActivityTypeRequest) => accountingApi.createTaxActivityType(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'tax-activity-types'] }); message.success('Вид діяльності створено'); },
    onError: () => { message.error('Помилка створення виду діяльності'); },
  });
}
export function useUpdateTaxActivityType() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: UpdateTaxActivityTypeRequest) => accountingApi.updateTaxActivityType(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'tax-activity-types'] }); message.success('Вид діяльності оновлено'); },
    onError: () => { message.error('Помилка оновлення виду діяльності'); },
  });
}
export function useDeleteTaxActivityType() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => accountingApi.deleteTaxActivityType(acqId),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'tax-activity-types'] }); message.success('Вид діяльності видалено'); },
    onError: () => { message.error('Помилка видалення виду діяльності'); },
  });
}

// =============================================
// Settlement Types
// =============================================
export function useSettlementTypes() {
  return useQuery({
    queryKey: ['accounting', 'settlement-types'],
    queryFn: () => accountingApi.getSettlementTypes(),
  });
}
export function useCreateSettlementType() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: CreateSettlementTypeRequest) => accountingApi.createSettlementType(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'settlement-types'] }); message.success('Тип розрахунків створено'); },
    onError: () => { message.error('Помилка створення типу розрахунків'); },
  });
}
export function useUpdateSettlementType() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: UpdateSettlementTypeRequest) => accountingApi.updateSettlementType(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'settlement-types'] }); message.success('Тип розрахунків оновлено'); },
    onError: () => { message.error('Помилка оновлення типу розрахунків'); },
  });
}
export function useDeleteSettlementType() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => accountingApi.deleteSettlementType(acqId),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'settlement-types'] }); message.success('Тип розрахунків видалено'); },
    onError: () => { message.error('Помилка видалення типу розрахунків'); },
  });
}

// =============================================
// Organization Departments
// =============================================
export function useOrgDepartments(acqOrgId: string) {
  return useQuery({
    queryKey: ['accounting', 'org-departments', acqOrgId],
    queryFn: () => accountingApi.getOrgDepartments(acqOrgId),
    enabled: !!acqOrgId,
  });
}
export function useCreateOrgDepartment() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: CreateOrgDepartmentRequest) => accountingApi.createOrgDepartment(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'org-departments'] }); message.success('Підрозділ створено'); },
    onError: () => { message.error('Помилка створення підрозділу'); },
  });
}
export function useUpdateOrgDepartment() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: UpdateOrgDepartmentRequest) => accountingApi.updateOrgDepartment(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'org-departments'] }); message.success('Підрозділ оновлено'); },
    onError: () => { message.error('Помилка оновлення підрозділу'); },
  });
}
export function useDeleteOrgDepartment() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => accountingApi.deleteOrgDepartment(acqId),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'org-departments'] }); message.success('Підрозділ видалено'); },
    onError: () => { message.error('Помилка видалення підрозділу'); },
  });
}

// =============================================
// VAT Rates (read-only)
// =============================================
export function useVatRates() {
  return useQuery({
    queryKey: ['accounting', 'vat-rates'],
    queryFn: () => accountingApi.getVatRates(),
    staleTime: 60 * 60 * 1000, // 1 hour — rarely changes
  });
}

// =============================================
// Counterparty Contracts
// =============================================
export function useContracts(acqFilters: ContractFilters) {
  return useQuery({
    queryKey: ['accounting', 'contracts', acqFilters],
    queryFn: () => accountingApi.getContracts(acqFilters),
    placeholderData: (acqPrev) => acqPrev,
  });
}
export function useCreateContract() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: CreateContractRequest) => accountingApi.createContract(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'contracts'] }); message.success('Договір створено'); },
    onError: () => { message.error('Помилка створення договору'); },
  });
}
export function useUpdateContract() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: UpdateContractRequest) => accountingApi.updateContract(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'contracts'] }); message.success('Договір оновлено'); },
    onError: () => { message.error('Помилка оновлення договору'); },
  });
}
export function useDeleteContract() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => accountingApi.deleteContract(acqId),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'contracts'] }); message.success('Договір видалено'); },
    onError: () => { message.error('Помилка видалення договору'); },
  });
}

// =============================================
// Currency Rates
// =============================================
export function useCurrencyRates(acqFilters: { currencyId?: string; dateFrom?: string; dateTo?: string }) {
  return useQuery({
    queryKey: ['accounting', 'currency-rates', acqFilters],
    queryFn: () => accountingApi.getCurrencyRates(acqFilters),
  });
}
export function useCreateCurrencyRate() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: CreateCurrencyRateRequest) => accountingApi.createCurrencyRate(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'currency-rates'] }); message.success('Курс валюти створено'); },
    onError: () => { message.error('Помилка створення курсу валюти'); },
  });
}
export function useUpdateCurrencyRate() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: UpdateCurrencyRateRequest) => accountingApi.updateCurrencyRate(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'currency-rates'] }); message.success('Курс валюти оновлено'); },
    onError: () => { message.error('Помилка оновлення курсу валюти'); },
  });
}
export function useDeleteCurrencyRate() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => accountingApi.deleteCurrencyRate(acqId),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'currency-rates'] }); message.success('Курс валюти видалено'); },
    onError: () => { message.error('Помилка видалення курсу валюти'); },
  });
}

// =============================================
// Nomenclature Prices
// =============================================
export function useNomenclaturePrices(acqFilters: { nomenclatureId?: string; priceTypeId?: string; page: number; pageSize: number }) {
  return useQuery({
    queryKey: ['accounting', 'nomenclature-prices', acqFilters],
    queryFn: () => accountingApi.getNomenclaturePrices(acqFilters),
    placeholderData: (acqPrev) => acqPrev,
  });
}
export function useCreateNomenclaturePrice() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: Omit<NomenclaturePriceItem, 'id' | 'priceTypeName' | 'nomenclatureName' | 'currencyCode'>) => accountingApi.createNomenclaturePrice(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'nomenclature-prices'] }); message.success('Ціну номенклатури створено'); },
    onError: () => { message.error('Помилка створення ціни номенклатури'); },
  });
}
export function useUpdateNomenclaturePrice() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: NomenclaturePriceItem) => accountingApi.updateNomenclaturePrice(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'nomenclature-prices'] }); message.success('Ціну номенклатури оновлено'); },
    onError: () => { message.error('Помилка оновлення ціни номенклатури'); },
  });
}
export function useDeleteNomenclaturePrice() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => accountingApi.deleteNomenclaturePrice(acqId),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'nomenclature-prices'] }); message.success('Ціну номенклатури видалено'); },
    onError: () => { message.error('Помилка видалення ціни номенклатури'); },
  });
}

// =============================================
// Accounting Policies
// =============================================
export function useAccountingPolicies(acqOrgId: string) {
  return useQuery({
    queryKey: ['accounting', 'accounting-policies', acqOrgId],
    queryFn: () => accountingApi.getAccountingPolicies(acqOrgId),
    enabled: !!acqOrgId,
  });
}
export function useCreateAccountingPolicy() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: Omit<AccountingPolicyItem, 'id' | 'organizationName'>) => accountingApi.createAccountingPolicy(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'accounting-policies'] }); message.success('Облікову політику створено'); },
    onError: () => { message.error('Помилка створення облікової політики'); },
  });
}
export function useUpdateAccountingPolicy() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: AccountingPolicyItem) => accountingApi.updateAccountingPolicy(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'accounting-policies'] }); message.success('Облікову політику оновлено'); },
    onError: () => { message.error('Помилка оновлення облікової політики'); },
  });
}
export function useDeleteAccountingPolicy() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => accountingApi.deleteAccountingPolicy(acqId),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'accounting-policies'] }); message.success('Облікову політику видалено'); },
    onError: () => { message.error('Помилка видалення облікової політики'); },
  });
}

// =============================================
// Account Balances
// =============================================
export function useAccountBalances(acqFilters: { organizationId?: string; dateFrom?: string; dateTo?: string }) {
  return useQuery({ queryKey: ['accounting', 'account-balances', acqFilters], queryFn: () => accountingApi.getAccountBalances(acqFilters) });
}

// =============================================
// Trial Balance (ОСВ)
// =============================================
export function useTrialBalance(acqFilters: TrialBalanceFilters) {
  return useQuery({
    queryKey: ['accounting', 'trial-balance', acqFilters],
    queryFn: () => accountingApi.getTrialBalance(acqFilters),
    enabled: !!acqFilters.dateFrom && !!acqFilters.dateTo,
  });
}

// =============================================
// Journal Entries (Проводки)
// =============================================
export function useJournalEntries(acqFilters: JournalEntryFilters) {
  return useQuery({ queryKey: ['accounting', 'journal-entries', acqFilters], queryFn: () => accountingApi.getJournalEntries(acqFilters), placeholderData: (acqPrev) => acqPrev });
}
export function useJournalEntriesByDocument(acqDocId: string | undefined) {
  return useQuery({ queryKey: ['accounting', 'journal-entries', 'doc', acqDocId], queryFn: () => accountingApi.getJournalEntriesByDocument(acqDocId!), enabled: !!acqDocId });
}

// =============================================
// Individuals
// =============================================
export function useIndividuals(acqFilters: IndividualFilters) {
  return useQuery({
    queryKey: ['accounting', 'individuals', acqFilters],
    queryFn: () => accountingApi.getIndividuals(acqFilters),
    placeholderData: (acqPrev) => acqPrev,
  });
}
export function useCreateIndividual() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: CreateIndividualRequest) => accountingApi.createIndividual(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'individuals'] }); message.success('Фіз. особу створено'); },
    onError: () => { message.error('Помилка створення фіз. особи'); },
  });
}
export function useUpdateIndividual() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: UpdateIndividualRequest) => accountingApi.updateIndividual(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'individuals'] }); message.success('Фіз. особу оновлено'); },
    onError: () => { message.error('Помилка оновлення фіз. особи'); },
  });
}
export function useDeleteIndividual() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => accountingApi.deleteIndividual(acqId),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'individuals'] }); message.success('Фіз. особу видалено'); },
    onError: () => { message.error('Помилка видалення фіз. особи'); },
  });
}

// =============================================
// Employees
// =============================================
export function useEmployees(acqFilters: EmployeeFilters) {
  return useQuery({
    queryKey: ['accounting', 'employees', acqFilters],
    queryFn: () => accountingApi.getEmployees(acqFilters),
    placeholderData: (acqPrev) => acqPrev,
  });
}
export function useCreateEmployee() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: CreateEmployeeRequest) => accountingApi.createEmployee(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'employees'] }); message.success('Співробітника створено'); },
    onError: () => { message.error('Помилка створення співробітника'); },
  });
}
export function useUpdateEmployee() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: UpdateEmployeeRequest) => accountingApi.updateEmployee(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'employees'] }); message.success('Співробітника оновлено'); },
    onError: () => { message.error('Помилка оновлення співробітника'); },
  });
}
export function useDeleteEmployee() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => accountingApi.deleteEmployee(acqId),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'employees'] }); message.success('Співробітника видалено'); },
    onError: () => { message.error('Помилка видалення співробітника'); },
  });
}

// Positions hooks видалено у Sprint 20+ Refactor Етап 4f.
// Position тепер meta-каталог (typeCode='position') — CRUD через DynamicCatalogItemsPage
// та catalogItemsApi. ReferencePicker для position — через unified /api/references/position.

// =============================================
// Fixed Assets
// =============================================
export function useFixedAssets() {
  return useQuery({
    queryKey: ['accounting', 'fixed-assets'],
    queryFn: () => accountingApi.getFixedAssets(),
  });
}
export function useCreateFixedAsset() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: CreateFixedAssetRequest) => accountingApi.createFixedAsset(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'fixed-assets'] }); message.success('Основний засіб створено'); },
    onError: () => { message.error('Помилка створення основного засобу'); },
  });
}
export function useUpdateFixedAsset() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: UpdateFixedAssetRequest) => accountingApi.updateFixedAsset(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'fixed-assets'] }); message.success('Основний засіб оновлено'); },
    onError: () => { message.error('Помилка оновлення основного засобу'); },
  });
}
export function useDeleteFixedAsset() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => accountingApi.deleteFixedAsset(acqId),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'fixed-assets'] }); message.success('Основний засіб видалено'); },
    onError: () => { message.error('Помилка видалення основного засобу'); },
  });
}

// =============================================
// Intangible Assets
// =============================================
export function useIntangibleAssets() {
  return useQuery({
    queryKey: ['accounting', 'intangible-assets'],
    queryFn: () => accountingApi.getIntangibleAssets(),
  });
}
export function useCreateIntangibleAsset() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: CreateIntangibleAssetRequest) => accountingApi.createIntangibleAsset(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'intangible-assets'] }); message.success('НМА створено'); },
    onError: () => { message.error('Помилка створення НМА'); },
  });
}
export function useUpdateIntangibleAsset() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: UpdateIntangibleAssetRequest) => accountingApi.updateIntangibleAsset(acqReq),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'intangible-assets'] }); message.success('НМА оновлено'); },
    onError: () => { message.error('Помилка оновлення НМА'); },
  });
}
export function useDeleteIntangibleAsset() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => accountingApi.deleteIntangibleAsset(acqId),
    onSuccess: () => { acqQueryClient.invalidateQueries({ queryKey: ['accounting', 'intangible-assets'] }); message.success('НМА видалено'); },
    onError: () => { message.error('Помилка видалення НМА'); },
  });
}
